import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, take } from "typed-redux-saga";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { DataProvider } from "@com.mgmtp.a12.client/client-core/lib/core/data";
import { Model, ModelActions, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { createEmptyDocument, FormModel, isFormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";

import { assertNonNullable, assertTrue } from "../../utilities/assert";

import { TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { isDocumentWithProcessMetadata } from "../workflows/data/processMetadata";

import { dataModels } from "./moduleResources";
import { WiedervorlageDocument } from "./types";

export const WiedervorlageInitialiser: DataProvider = {
	name: "WiedervorlagenInitialiser",
	canHandle({ dataHolder, operation }) {
		switch (operation) {
			case "load":
				return (
					dataHolder.descriptor.model === dataModels.WIEDERVORLAGE_D &&
					dataHolder.descriptor?.instance === NEW_INSTANCE_IDENTIFIER
				);
			default:
				return false;
		}
	},
	*provideData({ activityId, operation }): SagaIterator {
		if (operation === "load") {
			const activity = yield* select(ActivitySelectors.activityById(activityId));
			if (
				activity?.descriptor?.model &&
				activity.descriptor.model === dataModels.WIEDERVORLAGE_D &&
				activity?.descriptor?.instance === NEW_INSTANCE_IDENTIFIER &&
				activity.initiatingActivityId
			) {
				const documentModel = yield* select(
					ModelSelectors.modelInScene(
						{ activityId: activity.id, modelType: "document" },
						Model.isDocumentModel
					)
				);
				const formModel = yield* call(waitForFormModel, activity);

				assertNonNullable(documentModel);
				assertNonNullable(formModel);

				const emptyDocument = {
					id: NEW_INSTANCE_IDENTIFIER,
					modelId: documentModel.header.id,
					...createEmptyDocument(documentModel, formModel)
				};

				const initiatingActivity = yield* select(ActivitySelectors.activityById(activity.initiatingActivityId));

				const processId = initiatingActivity?.descriptor.processId;
				if (!processId) {
					throw new Error("Keine Antrags-ID in der Elternactivity gefunden.");
				}

				const data = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
				assertTrue(FormActivity.Data.SingleDocumentData.isInstance(data));
				assertTrue(isDocumentWithProcessMetadata(data.document));
				const { id, key, client, shortName } = data.document.root.processMetadata.fachverfahren;

				yield* put(
					ActivityActions.setData({
						data: {
							document: {
								...emptyDocument,
								root: {
									ProcessId: processId,
									Erledigt: false,
									correlatedFachverfahrenId: id,
									correlatedFachverfahrenKey: key,
									correlatedFachverfahrenClient: client,
									correlatedFachverfahrenShortName: shortName
								}
							} as WiedervorlageDocument
						},
						activityId
					})
				);
			}
		}
	}
};

function* waitForFormModel(activity: Activity): SagaGenerator<FormModel> {
	let formModel = yield* select(
		ModelSelectors.modelInScene({ activityId: activity.id, modelType: "form" }, isFormModel)
	);

	if (!formModel) {
		while (true) {
			yield* take(ModelActions.setModels);
			formModel = yield* select(
				ModelSelectors.modelInScene(
					{
						activityId: activity!.id,
						modelType: "form"
					},
					isFormModel
				)
			);
			if (formModel) {
				break;
			}
		}
	}
	return formModel;
}
